import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, Inject, Input, AfterViewInit, inject, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BACKLOG_INITIATIVE_LABELS, BREAK_IN_OUT_LABELS, FORECAST_LABELS, MEJ_CUTLINE_LABELS, PLAN_VS_FACT_LABELS, REPORT_AND_EWR_LABELS } from '../display-columns-dialog/column-labels.constant';
import { allOperators, breakInOutColumns, Column, ColumnData, DataType, FilterItem, forecastColumns, ganttChartColumns, initiativesColumns, linkedRecordsColumns, mejCutlineColumns, myApprovalsColumns, Operator, planVsFactColumns } from './constants';
import { Client, FilterRule, OnePAreaOwner, OnePFinanceStatusReadDTO, OnePMciProgramReadDTO } from '../../../api/api-clientInformation';
import { LanguageService } from '../../../services/language.service';
import { DisplayStorageService } from '../../../services/display-storage.service';
import { DictionariesService } from '../../../services/dictionaries.service';

@Component({
  selector: 'app-filter-dialog',
  standalone: false,
  templateUrl: './filter-dialog.component.html',
  styleUrls: ['./filter-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class FilterDialogComponent implements OnInit, AfterViewInit {
  displayStorageService = inject(DisplayStorageService);
  apiClient = inject(Client);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly languageService = inject(LanguageService);
  private areaObjects: OnePAreaOwner[] = [];
  private mciProgramObjects: OnePMciProgramReadDTO[] = [];
  private financeStatusObjects: OnePFinanceStatusReadDTO[] = [];
  selectedColumn!: ColumnData;
  selectedOperator!: string;
  filterValue: any;
  operators: string[] = [];
  selectItems: string[] = ['Option1', 'Option2'];

  DataType = DataType;
  filters: FilterItem[] = [
    { id: 1, column: '', operator: '', value: '', valueTo: '' }
  ];

  ngAfterViewInit() {
    if (this.data.pageKey === 'mej_cutline') {
      this.availableColumns = mejCutlineColumns;
    }
    else if (this.data.pageKey === 'break_in_out') {
      this.availableColumns = breakInOutColumns;
    } else if (this.data.pageKey === 'myapprovals') {
      this.availableColumns = myApprovalsColumns;
    } else if (this.data.pageKey === 'plan_vs_fact') {
      this.availableColumns = planVsFactColumns;
    } else if (this.data.pageKey === 'linked_records') {
      this.availableColumns = linkedRecordsColumns;
    } else if (this.data.pageKey === 'forecasts') {
      this.availableColumns = forecastColumns;
    } else if (this.data.pageKey === 'gantt_chart') {
      this.availableColumns = ganttChartColumns;
    } else {
      this.availableColumns = initiativesColumns;
    }
    this.loadAreaOptions();
    this.loadMciProgramOptions();
    this.loadFinanceStatusOptions();
    const savedFilter = localStorage.getItem(`filter_${this.data.pageKey}`);
    if (savedFilter) {
      const savedFilters = JSON.parse(savedFilter) as FilterRule[];
      let i = 1;
      savedFilters.forEach(x => {
        if (x.op !== '') {
          const filterItem: FilterItem = {
            id: i,
            operator: x.op!,
            column: x.field!,
            value: x.values![0],
            valueTo: x.values![1] ?? ''
          };
          if (x.field === 'MultiYear') {
            filterItem.value = x.values![0] === 'true' ? 'Yes' : 'No';
          }
          if (this.getColumnType(x.field!) === 'date') {
            const valueDate = new Date(x.values![0]);
            if (!isNaN(valueDate.getTime())) {
              filterItem.value = valueDate
            }
            const valueToDate = new Date(x.values![1]);

            if (!isNaN(valueToDate.getTime())) {
              filterItem.valueTo = valueToDate;
            }
          }
          if (this.getColumnType(x.field!) === 'multiSelect') {
            filterItem.value = x.values!;
          }

          i++;
          this.filters.push(filterItem);
        }
      });
      if (this.filters.length > 1)
        this.filters = this.filters.slice(1);
    }
  }

  addFilter(): void {
    const lastFilterId = this.filters[this.filters.length - 1].id;
    const newFilter: FilterItem = {
      id: lastFilterId + 1,
      column: '',
      operator: '',
      value: '',
      valueTo: ''
    };
    this.filters.push(newFilter);
  }

  removeFilter(id: number): void {
    if (this.filters.length > 1) {
      this.filters = this.filters.filter(f => f.id !== id);
    }
  }

  updateFilter(id: number, field: keyof FilterItem, value: string): void {
    const filter = this.filters.find(f => f.id === id);
    if (filter) {
      filter[field] = value as never;
      if (field === 'column') {
        filter.operator = '';
        filter.value = '';
        filter.valueTo = '';
        //this.availableColumns = initiativesColumns.filter(x => !this.filters.some(f => f.column === x.id))
      }
      if (field === 'operator') {
        filter.value = '';
        filter.valueTo = '';
      }
    }
  }

  private loadAreaOptions(): void {
    const areaFilter = this.availableColumns.find(x => x.id === 'AreaType');
    if (areaFilter)
      this.apiClient.getAreaOwners().subscribe({
        next: (areaOwners) => {
          this.areaObjects = areaOwners.filter(o => !!o.areaNameEn);
          areaFilter.values = this.areaObjects.map(owner => owner.areaNameEn) as string[];
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading area options for filters:', error);
        }
      });
  }

  private loadMciProgramOptions(): void {
    const mciFilter = this.availableColumns.find(x => x.id === 'MCIProgram');
    if (mciFilter)
      this.dictionariesService.mciPrograms$.subscribe({
        next: (programs) => {
          this.mciProgramObjects = programs.filter(p => !!p.programNameEn);
          mciFilter.values = this.mciProgramObjects.map(p => p.programNameEn) as string[];
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading MCI program options for filters:', error);
        }
      });
  }

  private loadFinanceStatusOptions(): void {
    const financeFilter = this.availableColumns.find(x => x.id === 'FinanceStatus');
    if (financeFilter)
      this.dictionariesService.financeStatuses$.subscribe({
        next: (statuses) => {
          this.financeStatusObjects = statuses.filter(s => !!s.statusNameEn);
          // Detail forecast filter should not offer "Carried Over" as a Finance Status option.
          if (this.data.pageKey === 'detail-forecast') {
            this.financeStatusObjects = this.financeStatusObjects.filter(s => s.statusNameEn?.trim().toLowerCase() !== 'carriedover');
          }
          financeFilter.values = this.financeStatusObjects.map(s => s.statusNameEn) as string[];
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading finance status options for filters:', error);
        }
      });
  }

  getLocalizedLabel(en?: string, kz?: string, ru?: string): string {
    const lang = this.languageService.currentLanguage();
    switch (lang) {
      case 'kk': return kz || en || '';
      case 'ru': return ru || en || '';
      default: return en || '';
    }
  }

  getLocalizedFilterOption(val: string, columnId: string): string {
    if (columnId === 'AreaType') {
      const obj = this.areaObjects.find(a => a.areaNameEn === val);
      return obj ? this.getLocalizedLabel(obj.areaNameEn, obj.areaNameKz, obj.areaNameRu) : val;
    }
    if (columnId === 'FinanceStatus') {
      const obj = this.financeStatusObjects.find(s => s.statusNameEn === val);
      return obj ? this.getLocalizedLabel(obj.statusNameEn, obj.statusNameKz, obj.statusNameRu) : val;
    }
    if (columnId === 'MCIProgram') {
      const obj = this.mciProgramObjects.find(p => p.programNameEn === val);
      return obj ? this.getLocalizedLabel(obj.programNameEn, obj.programNameKz, obj.programNameRu) : val;
    }
    return val;
  }

  getColumnType(columnId: string): string | null {
    const column = this.availableColumns.find(c => c.id === columnId);
    return column ? column.type : null;
  }

  getColumnValues(columnId: string): string[] {
    const column = this.availableColumns.find(c => c.id === columnId);
    return column?.values || [];
  }

  getOperatorsForColumn(columnId: string): Operator[] {
    const columnType = this.getColumnType(columnId);
    return columnType ? allOperators[columnType] : [];
  }

  needsTwoInputs(operator: string): boolean {
    return operator === 'between';
  }

  applyFilters(): void {
    const filterConditions: FilterRule[] = this.filters
      .filter(f => f.column && f.operator && f.value)
      .map(f => {
        const values: string[] = [];

        if (f.column === 'MultiYear') {
          return {
            field: f.column,
            op: f.operator,
            values: f.value === 'Yes' ? ['true'] : ['false']
          } as FilterRule;
        }

        if (this.getColumnType(f.column) === 'date' || this.getColumnType(f.column) === 'datetime') {
          if (f.value) {
            values.push(this.getDate(f.value as Date));
          }
          if (f.valueTo) {
            values.push(this.getDate(f.valueTo));
          }
        } else if (this.getColumnType(f.column) === 'multiSelect') {
          if (f.value) {
            values.push(...(f.value as string[]));
          }
        } else {
          if (f.value) {
            values.push(f.value as string);
          }

          if (f.valueTo) {
            values.push(f.valueTo as string);
          }
        }
        return {
          field: f.column,
          op: f.operator,
          values: values
        } as FilterRule;
      });
    this.displayStorageService.setFilters(`filter_${this.data.pageKey}`, filterConditions);
    this.dialogRef.close({ isFiltered: true });
  }

  getDate(dateString: string | Date) {
    if (!dateString) {
      return '';
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return '';
    }

    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  }


  clearFilters(): void {
    this.filters = [{ id: 1, column: '', operator: '', value: '', valueTo: '' }];
    localStorage.removeItem(`filter_${this.data.pageKey}`);
  }

  columnNameOptions: [string, string][] = [];
  availableColumns: Column[] = [];

  constructor(
    private dialogRef: MatDialogRef<FilterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
  }

  ngOnInit(): void {
    if (this.data.pageKey === 'initiatives') {
      this.columnNameOptions = Object.entries(BACKLOG_INITIATIVE_LABELS);
    }
    else if (this.data.pageKey === 'mej_cutline') {
      this.columnNameOptions = Object.entries(MEJ_CUTLINE_LABELS);
    }
    else if (this.data.pageKey === 'break_in_out') {
      this.columnNameOptions = Object.entries(BREAK_IN_OUT_LABELS);
    }
    else if (this.data.pageKey === 'linked_records') {
      this.columnNameOptions = Object.entries(REPORT_AND_EWR_LABELS);
    }
    else if (this.data.pageKey === 'forecasts') {
      this.columnNameOptions = Object.entries(FORECAST_LABELS);
    }
    else if (this.data.pageKey === 'plan_vs_fact') {
      this.columnNameOptions = Object.entries(PLAN_VS_FACT_LABELS);
    }
    else if (this.data.pageKey === 'gantt_chart') {
      this.columnNameOptions = Object.entries(BACKLOG_INITIATIVE_LABELS);
    }
    // this.columnNameOptions = this.data.pageKey == 'mej_cutline' ? Object.entries(MEJ_CUTLINE_LABELS) : Object.entries(BACKLOG_INITIATIVE_LABELS);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
